/**
 * 非同期データの読み込み。
 *
 * 取得中に依存が変わった場合は古い結果を捨てる（月を素早く切り替えたときに、
 * 前の月の結果が後から上書きするのを防ぐ）。
 */

import { useEffect, useState } from 'react';

export interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: unknown;
  reload: () => void;
}

/**
 * @param loader 取得処理。呼び出しのたびに新しい関数になるため依存には含めない
 * @param deps   これが変わったら取り直す（月・従業員など）
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  // 依存の配列そのものは毎回別のインスタンスになるため、内容を文字列にして比較する
  const key = JSON.stringify(deps);

  // どの依存で取得した結果かを一緒に持つ。月や日付を切り替えた直後に、前の対象の
  // 内容をそのまま表示・保存してしまうのを防ぐ
  const [result, setResult] = useState<{ key: string; data: T | null }>({ key, data: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    // 前の対象で出ていたエラーを引きずらない
    setError(null);
    loader()
      .then((data) => {
        if (cancelled) return;
        setResult({ key, data });
        setError(null);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setError(cause);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, nonce]);

  return {
    data: result.key === key ? result.data : null,
    loading,
    error,
    reload: () => setNonce((value) => value + 1),
  };
}
